/**
 * SettingsTab — container for admin settings + user-accessible Manage API.
 * Admins see all sub-tabs; regular users see only "Manage API".
 */
import React, { useState } from 'react';
import { Trade } from '../../types/Trade';
import { useAuth } from '../../auth/AuthContext';
import AddTab from '../AddTab/AddTab';
import DeleteTab from '../DeleteTab/DeleteTab';
import ValidationTab from '../ValidationTab/ValidationTab';
import ManageApiTab from '../SearchTab/SearchTab';
import ProgressTab from '../ProgressTab/ProgressTab';
import ActivityLog from '../../shared/ActivityLog/ActivityLog';
import UserManagementPanel from '../../shared/UserManagement/UserManagementPanel';
import DocsTab from '../DocsTab/DocsTab';
import CronHealthTab from '../CronHealthTab/CronHealthTab';
import './SettingsTab.css';

type SettingsSubTab =
  | 'manage'
  | 'explorer'
  | 'validation'
  | 'cron'
  | 'api'
  | 'progress'
  | 'activity'
  | 'users'
  | 'docs';

const ADMIN_TABS: { key: SettingsSubTab; label: string }[] = [
  { key: 'manage', label: 'Architecture & Coin Management' },
  { key: 'explorer', label: 'Explorer' },
  { key: 'validation', label: 'Validation' },
  { key: 'cron', label: 'Cron Health' },
  { key: 'api', label: 'Manage API' },
  { key: 'progress', label: 'Progress' },
  { key: 'activity', label: 'Activity Log' },
  { key: 'users', label: 'Users' },
  { key: 'docs', label: 'Docs' },
];

const USER_TABS: { key: SettingsSubTab; label: string }[] = [
  { key: 'api', label: 'Manage API' },
  { key: 'docs', label: 'Docs' },
];

interface SettingsTabProps {
  trades: Trade[];
  onDelete: (id: string) => void;
}

export default function SettingsTab({ trades, onDelete }: SettingsTabProps) {
  const { session } = useAuth();
  const isAdmin = session?.role === 'admin';
  const tabs = isAdmin ? ADMIN_TABS : USER_TABS;
  const [subTab, setSubTab] = useState<SettingsSubTab>(tabs[0].key);

  return (
    <div className="settings-page">
      {/* Only show the sub-tab bar if there's more than one tab */}
      {tabs.length > 1 && (
        <div className="settings-subtab-bar">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`settings-subtab ${subTab === t.key ? 'active' : ''}`}
              onClick={() => setSubTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="settings-content">
        {subTab === 'manage' && isAdmin && (
          <AddTab allowedViews={['arch', 'manage']} />
        )}
        {subTab === 'explorer' && isAdmin && (
          <DeleteTab trades={trades} onDelete={onDelete} />
        )}
        {subTab === 'validation' && isAdmin && <ValidationTab />}
        {subTab === 'cron' && isAdmin && <CronHealthTab />}
        {subTab === 'api' && <ManageApiTab />}
        {subTab === 'progress' && isAdmin && <ProgressTab />}
        {subTab === 'activity' && isAdmin && <ActivityLog />}
        {subTab === 'users' && isAdmin && <UserManagementPanel />}
        {subTab === 'docs' && <DocsTab />}
      </div>
    </div>
  );
}
